import React, { useState, useMemo, useEffect } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { Button } from "../../components/ui/button";
import { FileText, Calendar } from "lucide-react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import axios from "axios";
import { toast, Toaster } from "sonner";

const Sales = () => {
  // Date range: first day of current month to today
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateRange, setDateRange] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: today,
      key: "selection",
    },
  ]);

  const token = localStorage.getItem("adminToken");
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch bookings
  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          "https://wavetechservices.in/admin/bookings",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBookings(res.data.data);
      } catch (err) {
        console.error("Error fetching bookings:", err);
        toast.error("Failed to fetch bookings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [token]);

  // Group bookings whenever bookings data is updated
  useEffect(() => {
    if (bookings.length > 0) {
      const sortedData = groupBookingsByDate(bookings);
      setAllData(sortedData);
    }
  }, [bookings]);

  function groupBookingsByDate(bookings) {
    const dateCounts = {};

    bookings.forEach((booking) => {
      const date = booking?.createdAt?.split("T")[0];
      dateCounts[date] = (dateCounts[date] || 0) + 1;
    });

    const result = Object.entries(dateCounts).map(([date, value]) => ({
      date,
      value,
    }));

    result.sort((a, b) => new Date(a.date) - new Date(b.date));

    return result;
  }

  // Filter data based on selected date range
  const filteredData = useMemo(() => {
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;

    return allData.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate <= end;
    });
  }, [dateRange, allData]);

  const totalValue = useMemo(
    () => filteredData.reduce((sum, item) => sum + item.value, 0),
    [filteredData]
  );

  const chartConfig = {
    value: {
      label: "Bookings",
      color: "#0a67f2",
    },
  };


  console.log("Booking length : " , bookings.length)
  return (
    <Card>
      <Toaster />
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>Total Booking</CardTitle>
          <CardDescription>
            Showing booking data for selected date range
          </CardDescription>
          <div className="flex gap-3 my-3">
            <Button>
              Download Data
              <FileText className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row">
          <div className="relative flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Total Bookings
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCalendar(!showCalendar)}
                className="text-muted-foreground"
              >
                <Calendar className="mr-2 h-4 w-4" />
                {format(dateRange[0].startDate, "MMM dd, yyyy")} -{" "}
                {format(dateRange[0].endDate, "MMM dd, yyyy")}
              </Button>
            </div>
            <span className="text-lg font-bold leading-none sm:text-3xl">
              {isLoading ? "Loading..." : totalValue.toLocaleString()}
            </span>

            {showCalendar && (
              <div className="absolute right-0 top-full z-50 mt-2 shadow-lg">
                <DateRange
                  editableDateInputs={true}
                  onChange={(item) => {
                    setDateRange([item.selection]);
                    setShowCalendar(false);
                  }}
                  moveRangeOnFirstSelection={false}
                  ranges={dateRange}
                  maxDate={new Date()}
                  locale={enUS}
                />
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-2 sm:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-[250px] text-lg text-muted-foreground">
            Loading chart...
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[250px] w-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={filteredData}
                margin={{
                  left: 12,
                  right: 12,
                  top: 12,
                  bottom: 12,
                }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value) => {
                    return format(new Date(value), "MMM dd");
                  }}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      className="w-[150px]"
                      labelFormatter={(value) => {
                        return format(new Date(value), "MMM dd, yyyy");
                      }}
                      formatter={(value) => [`${value} bookings`, ""]}
                    />
                  }
                />
                <Bar
                  dataKey="value"
                  fill={chartConfig.value.color}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default Sales;
